import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { Database } from "@/integrations/supabase/types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface LikeWithProfile {
  id: string;
  liker_id: string;
  created_at: string;
  profile: Profile;
}

export const useWhoLikedMe = () => {
  const { user, profile } = useAuthContext();
  const [likes, setLikes] = useState<LikeWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPremium = profile?.is_premium ?? false;

  const fetchLikes = useCallback(async () => {
    if (!user) {
      setLikes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get all likes where current user is the liked_id
      // Exclude users that we've already matched with
      const { data: likeData, error: likeError } = await supabase
        .from("likes")
        .select(`
          id,
          liker_id,
          created_at
        `)
        .eq("liked_id", user.id)
        .order("created_at", { ascending: false });

      if (likeError) {
        throw likeError;
      }

      if (!likeData || likeData.length === 0) {
        setLikes([]);
        setLoading(false);
        return;
      }

      // Get matches to exclude already matched users
      const { data: matchData } = await supabase
        .from("matches")
        .select("user1_id, user2_id")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

      const matchedUserIds = new Set<string>();
      matchData?.forEach((match) => {
        if (match.user1_id === user.id) {
          matchedUserIds.add(match.user2_id);
        } else {
          matchedUserIds.add(match.user1_id);
        }
      });

      // Filter out matched users
      const filteredLikes = likeData.filter(
        (like) => !matchedUserIds.has(like.liker_id)
      );

      if (filteredLikes.length === 0) {
        setLikes([]);
        setLoading(false);
        return;
      }

      // Fetch profiles for the likers
      const likerIds = filteredLikes.map((l) => l.liker_id);
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .in("id", likerIds);

      if (profileError) {
        throw profileError;
      }

      const profileMap = new Map<string, Profile>();
      profileData?.forEach((p) => profileMap.set(p.id, p));

      const likesWithProfiles: LikeWithProfile[] = filteredLikes
        .filter((like) => profileMap.has(like.liker_id))
        .map((like) => ({
          id: like.id,
          liker_id: like.liker_id,
          created_at: like.created_at,
          profile: profileMap.get(like.liker_id)!,
        }));

      setLikes(likesWithProfiles);
    } catch (err) {
      console.error("Error fetching likes:", err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  // Subscribe to new likes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("who-liked-me")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "likes",
          filter: `liked_id=eq.${user.id}`,
        },
        () => {
          fetchLikes();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchLikes]);

  return {
    likes,
    loading,
    error,
    isPremium,
    count: likes.length,
    refetch: fetchLikes,
  };
};
